import { useState } from "react";
import MainContainer from "components/sections/MainContainer";
import MetaHead from "components/base/MetaHead";
import Image from "components/base/Image";
import Container from "components/base/Container";
import Trans, { useFormatMessage } from "components/base/Trans";
import TextHeader from "components/sections/TextHeader";
import FloatingCTA from "components/blocks/FloatingCTA";
import ImageSequenceSection from "components/sections/ImageSequenceSection";
import ImageSection2 from "components/sections/ImageSection2";
import TextSection from "components/sections/TextSection";
import SmallTextSection from "components/sections/SmallTextSection";
import WheelsSection from "components/sections/WheelsSection";
import SkidsSection from "components/sections/SkidsSection";
import BgChangeSection from "components/sections/BgChangeSection";
import TextVideoColumns from "components/sections/TextVideoColumns";
import LandGearComparisonSection from "components/sections/LandGearComparisonSection";
import ExteriorDimensionsSection from "components/sections/ExteriorDimensionsSection";
import NavFooter from "components/sections/NavFooter";
import HeroStreamVideoSection from "components/sections/HeroStreamVideoSection";
import GallerySection from "components/sections/GallerySection";
import HorizontalSlideSection from "components/sections/HorizontalSlideSection";
import { useShowCTA } from "utils/hooks";

export default function Exterior() {
  const formatMessage = useFormatMessage();

  const [lastSectionVisible, setLastSectionVisible] = useState(false);
  const { headerRef, showCTA } = useShowCTA(lastSectionVisible);

  return (
    <MainContainer
      navStyle={{ gradient: false, inverted: false }}
      updateVisible={setLastSectionVisible}
    >
      <MetaHead
        title="LBL_EXTERIOR_META_TITLE"
        keywords={["LBL_META_KEYWORD_3"]}
        description="LBL_EXTERIOR_META_DESC"
        imageUrl="/images/meta/exterior.jpg"
      />
      <div ref={headerRef}>
        <TextHeader
          inverted
          page={<Trans id="LBL_EXTERIOR_PAGE_TITLE" />}
          title={<Trans id="LBL_EXTERIOR_TITLE" />}
          description={<Trans id="LBL_EXTERIOR_DESCRIPTION" />}
        />
      </div>
      <HeroStreamVideoSection
        height={["40vh", null, "100vh"]}
        minHeight={["100%", null, "700px"]}
        videoSrc="https://vz-49830c06-001.b-cdn.net/4e8b77a0-c521-4f89-86e2-708d2e38842a/playlist.m3u8"
        mobileVideoSrc="https://vz-49830c06-001.b-cdn.net/4e8b77a0-c521-4f89-86e2-708d2e38842a/playlist.m3u8"
        metadata={{
          video_title: formatMessage({
            id: "LBL_EXTERIOR_META_TITLE",
          }),
          video_id: "exterior-header",
        }}
        mobileMetadata={{
          video_title: formatMessage({
            id: "LBL_EXTERIOR_META_TITLE",
          }),
          video_id: "exterior-mobile-header",
        }}
        bgOverlay="transparent"
      />
      <TextSection
        title={<Trans id="LBL_EXTERIOR_INTRO_TITLE" />}
        text={<Trans id="LBL_EXTERIOR_INTRO_DESC" />}
      />
      <WheelsSection />
      <GallerySection
        images={[
          { src: "/images/exterior2/wheels-05.jpg", alt: "wheels Gallery" },
          { src: "/images/exterior2/wheels-06.jpg", alt: "wheels Detail 1" },
          { src: "/images/exterior2/wheels-07.jpg", alt: "wheels Detail 2" },
        ]}
      />
      <SkidsSection />
      <GallerySection
        images={[
          { src: "/images/exterior2/skids-04.jpg", alt: "skids Gallery" },
          { src: "/images/exterior2/skids-05.jpg", alt: "skids Detail 1" },
          { src: "/images/exterior2/skids-06.jpg", alt: "skids Detail 2" },
        ]}
      />
      <LandGearComparisonSection
        images={[
          {
            desktop: "/images/exterior2/comparison-wheels-01.jpg",
            mobile: "/images/exterior2/comparison-wheels-01-mobile.jpg",
          },
          {
            desktop: "/images/exterior2/comparison-skids-01.jpg",
            mobile: "/images/exterior2/comparison-skids-01-mobile.jpg",
          },
        ]}
      />
      <SmallTextSection
        inverted
        title={<Trans id="LBL_EXTERIOR_LUGGAGE_TITLE_1" />}
        subtitle={<Trans id="LBL_EXTERIOR_LUGGAGE_SUBTITLE_1" />}
        text={<Trans id="LBL_EXTERIOR_LUGGAGE_DESC_1" />}
        textAlign="left"
      />
      <TextVideoColumns
        topTitle={<Trans id="LBL_EXTERIOR_LUGGAGE_TITLE_2" />}
        topDescription={<Trans id="LBL_EXTERIOR_LUGGAGE_DESC_2" />}
        topVideo="https://vz-49830c06-001.b-cdn.net/030ac759-4503-4ab6-abce-7feeb0446112/playlist.m3u8"
        videoMetadata={{
          video_title: formatMessage({ id: "LBL_EXTERIOR_LUGGAGE_TITLE_2" }),
          video_id: "exterior-luggage",
        }}
        bottomTitle={<Trans id="LBL_EXTERIOR_LUGGAGE_TITLE_3" />}
        bottomDescription={<Trans id="LBL_EXTERIOR_LUGGAGE_DESC_3" />}
        bottomImage="/images/exterior2/img-luggage-02.jpg"
        pt={0}
      />
      <BgChangeSection
        animationId="access"
        initialColor="white"
        finalColor="black.900"
        minH="100%"
      >
        <SmallTextSection
          inverted
          bg={["white", null, "transparent"]}
          title={<Trans id="LBL_EXTERIOR_ACCESS_TITLE" />}
          subtitle={<Trans id="LBL_EXTERIOR_ACCESS_SUBTITLE" />}
          text={<Trans id="LBL_EXTERIOR_ACCESS_DESC" />}
          textAlign="left"
        />

        <Container pt={0}>
          <Image
            alt="Doors"
            src="/images/exterior2/doors.jpg"
            objectFit="cover"
            objectPosition="center"
            minHeight={["450px", null, "650px"]}
            height={["450px", null, "90vh"]}
          />
        </Container>
      </BgChangeSection>
      <SmallTextSection
        bg="black.900"
        title={<Trans id="LBL_EXTERIOR_PANORAMIC_TITLE_1" />}
        subtitle={<Trans id="LBL_EXTERIOR_PANORAMIC_SUBTITLE_1" />}
        text={<Trans id="LBL_EXTERIOR_PANORAMIC_DESC_1" />}
        textAlign="left"
      />
      <ImageSection2
        bg="black.900"
        image="/images/exterior2/roof/desktop/001.jpg"
        title={formatMessage({ id: "LBL_EXTERIOR_PANORAMIC_TITLE_2" })}
        description={formatMessage({ id: "LBL_EXTERIOR_PANORAMIC_DESC_2" })}
      />
      <SmallTextSection
        bg="black.900"
        title={<Trans id="LBL_EXTERIOR_BLADES_TITLE_1" />}
        subtitle={<Trans id="LBL_EXTERIOR_BLADES_SUBTITLE_1" />}
        text={<Trans id="LBL_EXTERIOR_BLADES_DESC_1" />}
        textAlign="left"
      />

      <ImageSequenceSection
        animationId="blades-desktop"
        url="/images/exterior/blades/desktop/"
        width={1800}
        height={1200}
        numFrames={48}
        bg="black.900"
        fullScreen
        display={["none", null, "block"]}
        title={formatMessage({ id: "LBL_EXTERIOR_BLADES_TITLE_2" })}
        description={formatMessage({ id: "LBL_EXTERIOR_BLADES_DESC_2" })}
      />

      <ImageSequenceSection
        animationId="blades-mobile"
        url="/images/exterior/blades/mobile/"
        width={900}
        height={1350}
        numFrames={48}
        bg="black.900"
        display={["block", null, "none"]}
        title={formatMessage({ id: "LBL_EXTERIOR_BLADES_TITLE_2" })}
        description={formatMessage({ id: "LBL_EXTERIOR_BLADES_DESC_2" })}
      />
      <ExteriorDimensionsSection />
      <TextSection
        pt={0}
        title={<Trans id="LBL_EXTERIOR_FOOTER_TITLE" />}
        text={<Trans id="LBL_EXTERIOR_FOOTER_DESC" />}
      />
      <HorizontalSlideSection
        bg="black.900"
        baseSrc="/images/exterior2/gallery/"
        items={[
          "01",
          "02-vert",
          "03",
          "04-vert",
          "05",
          "06",
          "07-vert",
          "08",
          "09-vert",
          "10",
          "11-vert",
          "12",
          "13-vert",
          "14",
          "15-vert",
          "16",
          "17-vert",
          "19-vert",
          "20",
          "21-vert",
          "22-vert",
        ]}
      />
      <NavFooter
        previous={<Trans id="LBL_MENU_HOME" />}
        pathPrevious="/"
        bgPrevious="/images/nav-footer/home.jpg"
        next={<Trans id="LBL_MENU_INTERIOR" />}
        pathNext="/interior-concept"
        bgNext="/images/nav-footer/interior.jpg"
      />
      <FloatingCTA show={showCTA} />
    </MainContainer>
  );
}
